import type { Artifact, ItemDetail, ItemSummary, Me, Paged, SearchParams, TopicDetail, TopicSummary } from './types';

export type ApiErrorKind = 'unauthorized' | 'forbidden' | 'notFound' | 'network' | 'server';

/** An API failure classified for plain-language messages (see components/ErrorPanel). */
export class ApiError extends Error {
  constructor(
    public readonly kind: ApiErrorKind,
    public readonly status?: number,
    message?: string,
  ) {
    super(message ?? kind);
  }
}

export type TokenProvider = () => Promise<string | undefined>;

export function classifyStatus(status: number): ApiErrorKind {
  if (status === 401) return 'unauthorized';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'notFound';
  return 'server';
}

/**
 * Thin client over the KB REST API. Every call sends the user's own access token, refreshed by the
 * token provider before use (KB tokens live about five minutes).
 */
export class KbClient {
  constructor(
    private readonly baseUrl: string,
    private readonly getToken: TokenProvider,
    private readonly fetchImpl: typeof fetch = (...args) => fetch(...args),
  ) {}

  private async request<T>(path: string, init?: RequestInit): Promise<T> {
    const token = await this.getToken();
    let response: Response;
    try {
      response = await this.fetchImpl(`${this.baseUrl}${path}`, {
        ...init,
        headers: {
          Accept: 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...init?.headers,
        },
      });
    } catch (cause) {
      throw new ApiError('network', undefined, cause instanceof Error ? cause.message : undefined);
    }
    if (!response.ok) throw new ApiError(classifyStatus(response.status), response.status);
    return (await response.json()) as T;
  }

  private static tenant(tenant: string): string {
    return encodeURIComponent(tenant);
  }

  me(): Promise<Me> {
    return this.request('/me');
  }

  searchItems(tenant: string, params: SearchParams): Promise<Paged<ItemSummary>> {
    return this.request(`/${KbClient.tenant(tenant)}/knowledgeitems/summary?${searchQuery(params)}`);
  }

  topics(tenant: string, page = 1, pageSize = 50): Promise<Paged<TopicSummary>> {
    return this.request(`/${KbClient.tenant(tenant)}/topics/summary?page=${page}&pageSize=${pageSize}`);
  }

  topic(tenant: string, id: string): Promise<TopicDetail> {
    return this.request(`/${KbClient.tenant(tenant)}/topics/${encodeURIComponent(id)}/detail`);
  }

  item(tenant: string, id: string): Promise<ItemDetail> {
    return this.request(`/${KbClient.tenant(tenant)}/knowledgeitems/${encodeURIComponent(id)}`);
  }

  artifact(tenant: string, id: string): Promise<Artifact> {
    return this.request(`/${KbClient.tenant(tenant)}/artifacts/${encodeURIComponent(id)}`);
  }

  /** Short-lived presigned URL; the KB records the access in its audit log. */
  async downloadUrl(tenant: string, artifactId: string): Promise<string> {
    const body = await this.request<{ downloadUrl?: string }>(
      `/${KbClient.tenant(tenant)}/artifacts/${encodeURIComponent(artifactId)}/download`,
    );
    if (!body.downloadUrl) throw new ApiError('server', undefined, 'no download URL');
    return body.downloadUrl;
  }

  /** Small files only (previews): the bytes stream through the KB with the user's token. */
  async content(tenant: string, artifactId: string, maxBytes: number): Promise<Blob> {
    const token = await this.getToken();
    let response: Response;
    try {
      response = await this.fetchImpl(
        `${this.baseUrl}/${KbClient.tenant(tenant)}/artifacts/${encodeURIComponent(artifactId)}/content`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
    } catch (cause) {
      throw new ApiError('network', undefined, cause instanceof Error ? cause.message : undefined);
    }
    if (!response.ok) throw new ApiError(classifyStatus(response.status), response.status);
    const blob = await response.blob();
    if (blob.size > maxBytes) throw new ApiError('server', undefined, 'preview too large');
    return blob;
  }
}

export function searchQuery(params: SearchParams): string {
  const query = new URLSearchParams();
  if (params.q?.trim()) query.set('q', params.q.trim());
  if (params.types?.length) query.set('types', params.types.join(','));
  if (params.pilot) query.set('pilot', params.pilot);
  if (params.sector) query.set('sector', params.sector);
  if (params.sensitivityLevel) query.set('sensitivityLevel', params.sensitivityLevel);
  query.set('sort', params.sort ?? 'updated_desc');
  query.set('page', String(params.page ?? 1));
  query.set('pageSize', String(params.pageSize ?? 20));
  return query.toString();
}
